import { Status } from '~api/som_search';

import { Button, FilterTagBar } from '@ingrid/luterkort-react';

import { useLocalizeCountry } from '~src/i18n/localized-country';
import { useLocalizedDateRange } from '~src/i18n/localized-date';
import { LocalizationKey, useTranslation } from '~src/i18n/use-translation';
import { useShippingMethodListQuery } from '~src/main/queries';
import { getStatusLabel } from '~src/utils/status-helpers';

import { useShipmentListFilter } from './useShipmentListFilter';

export function Tags() {
  const { filtering, setFiltering, advancedFiltering, setAdvancedFiltering, clearFilters } = useShipmentListFilter();
  const { data: shippingMethods = [] } = useShippingMethodListQuery();
  const { t } = useTranslation();

  const localizeDateRange = useLocalizedDateRange({ short: true, time: true });
  const localizeCountry = useLocalizeCountry();

  return (
    <FilterTagBar.Root>
      <FilterTagBar.Button
        value={filtering.excludedSchedulePickupIds ? t('common.unableToSchedulePickup') : undefined}
        onClick={() => setFiltering(p => ({ ...p, excludedSchedulePickupIds: undefined }))}
        name="excludedSchedulePickupIds"
      />

      <FilterTagBar.Button
        value={filtering.excludedPrintIds ? t('common.unableToPrint') : undefined}
        onClick={() => setFiltering(p => ({ ...p, excludedPrintIds: undefined }))}
        name="excludedPrintIds"
      />

      <FilterTagBar.Button
        value={filtering.excludedBookIds ? t('common.unableToBook') : undefined}
        onClick={() => setFiltering(p => ({ ...p, excludedBookIds: undefined }))}
        name="excludedBookIds"
      />

      <FilterTagBar.Button
        onClick={() => setFiltering(p => ({ ...p, carriers: [] }))}
        label={t('common.carrier')}
        name="carriers"
        value={filtering.carriers
          .map(method => shippingMethods.find(m => m.id === method))
          .map(method => `${method?.name}`)}
      />

      <FilterTagBar.Button
        value={filtering.statuses.map(status => t(getStatusLabel(status as Status)))}
        onClick={() => setFiltering(p => ({ ...p, statuses: [] }))}
        label={t('common.status')}
        name="status"
      />

      <FilterTagBar.Button
        value={advancedFiltering.deliveryCountries.map(country => localizeCountry(country)!)}
        onClick={() => setAdvancedFiltering(p => ({ ...p, deliveryCountries: [] }))}
        label={t('shipments.list.deliveryCountries')}
        name="deliveryCountries"
      />

      <FilterTagBar.Button
        value={advancedFiltering.originCountries.map(country => localizeCountry(country)!)}
        onClick={() => setAdvancedFiltering(p => ({ ...p, originCountries: [] }))}
        label={t('shipments.list.originCountries')}
        name="originCountries"
      />

      <FilterTagBar.Button
        value={advancedFiltering.directionTypes.map(dir => t(`shipments.list.directionType.${dir}` as LocalizationKey))}
        onClick={() => setAdvancedFiltering(p => ({ ...p, directionTypes: [] }))}
        label={t('shipments.list.directionTypes')}
        name="directionTypes"
      />

      <FilterTagBar.Button
        onClick={() => setAdvancedFiltering(p => ({ ...p, createdAtRange: undefined }))}
        value={advancedFiltering.createdAtRange ? localizeDateRange(advancedFiltering.createdAtRange) : undefined}
        label={t('common.createdAt')}
        name="createdAt"
      />

      <FilterTagBar.Button
        onClick={() => setAdvancedFiltering(p => ({ ...p, shippingDateRange: undefined }))}
        value={advancedFiltering.shippingDateRange ? localizeDateRange(advancedFiltering.shippingDateRange) : undefined}
        label={t('shipments.list.shippingDate')}
        name="shippingDate"
      />

      <Button onClick={clearFilters} variant="quaternary" size="sm">
        {t('common.clearFilters')}
      </Button>
    </FilterTagBar.Root>
  );
}
